import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import {
  CATEGORY_ORDER,
  CATEGORY_LABELS,
  DEFAULT_CATEGORY,
} from './calendarCategories';

/**
 * Modal to add Google calendars to the sidebar. Checking a row adds it
 * to the sidebar; the dropdown on each row assigns a TSP category.
 *
 * Props:
 *  - open: boolean
 *  - calendars: array of { id, summary, primary, backgroundColor }
 *  - initialAddedIds: Set<string>
 *  - initialCategories: { [calendarId]: categoryKey }
 *  - onClose(): cancel
 *  - onSave(nextAddedIds: Set<string>, nextCategories: object): commit
 */
const CalendarPickerDialog = ({
  open,
  calendars,
  initialAddedIds,
  initialCategories,
  onClose,
  onSave,
}) => {
  const [draftAdded, setDraftAdded] = useState(() => new Set(initialAddedIds));
  const [draftCategories, setDraftCategories] = useState(() => ({ ...initialCategories }));

  useEffect(() => {
    if (open) {
      setDraftAdded(new Set(initialAddedIds));
      setDraftCategories({ ...initialCategories });
    }
  }, [open, initialAddedIds, initialCategories]);

  const toggle = (id) => {
    setDraftAdded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Seed a default category when a row first becomes checked.
    setDraftCategories((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: DEFAULT_CATEGORY };
    });
  };

  const setCategory = (id, category) => {
    setDraftCategories((prev) => ({ ...prev, [id]: category }));
  };

  const orderedCalendars = [
    ...calendars.filter((c) => c.primary),
    ...calendars.filter((c) => !c.primary),
  ];

  const cellBase = {
    padding: '8px 12px',
    fontSize: 13,
    color: '#344054',
    borderBottom: '1px solid #EAECF0',
    verticalAlign: 'middle',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        Add Calendar From Google
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {orderedCalendars.length === 0 ? (
          <p style={{ margin: 0, color: '#667085' }}>
            No calendars found on your Google account.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th
                  style={{
                    ...cellBase,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#475467',
                    background: '#F9FAFB',
                  }}
                >
                  Google Calendar Name
                </th>
                <th
                  style={{
                    ...cellBase,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#475467',
                    background: '#F9FAFB',
                    width: 220,
                  }}
                >
                  TSP Calendar Category
                </th>
              </tr>
            </thead>
            <tbody>
              {orderedCalendars.map((cal) => {
                const checked = draftAdded.has(cal.id);
                const category = draftCategories[cal.id] || DEFAULT_CATEGORY;
                return (
                  <tr key={cal.id}>
                    <td style={cellBase}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(cal.id)}
                          style={{
                            accentColor: cal.backgroundColor || '#356AC3',
                            width: 16,
                            height: 16,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: 3,
                            background: cal.backgroundColor || '#356AC3',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={cal.summary}
                        >
                          {cal.summary}
                          {cal.primary && (
                            <span style={{ color: '#667085', marginLeft: 6 }}>(primary)</span>
                          )}
                        </span>
                      </label>
                    </td>
                    <td style={cellBase}>
                      <Select
                        size="small"
                        value={category}
                        onChange={(e) => setCategory(cal.id, e.target.value)}
                        disabled={!checked}
                        fullWidth
                        sx={{ fontSize: 13 }}
                      >
                        {CATEGORY_ORDER.map((key) => (
                          <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                            {CATEGORY_LABELS[key]}
                          </MenuItem>
                        ))}
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(draftAdded, draftCategories)}
          disabled={orderedCalendars.length === 0}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarPickerDialog;
